import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supportTicketService } from 'api/services/SupportTicketService';
import type { SupportTicketViewModel } from 'api/types/support-ticket';
import Button from 'components/Button';

const PAGE_SIZE = 10;

export interface SupportTicketListProps {
  detailPath?: (id: string) => string;
}

const SupportTicketList: React.FC<SupportTicketListProps> = ({
  detailPath = (id) => `/teknik-destek/ticket/${id}`,
}) => {
  const [tickets, setTickets] = useState<SupportTicketViewModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const loadPage = useCallback(async (page: number) => {
    setLoading(true);
    setError(null);
    try {
      const result = await supportTicketService.getMyTickets({ pagination: { pageNumber: page, pageSize: PAGE_SIZE } });
      if (result.isSuccess && result.data) {
        const list = Array.isArray(result.data) ? result.data : [];
        setTickets(list);
        const total = result.pagination?.totalPageCount ?? 1;
        setTotalPages(total);
      } else {
        setTickets([]);
        setError(result.message ?? 'Talepler yüklenemedi.');
      }
    } catch (err) {
      setTickets([]);
      setError(err instanceof Error ? err.message : 'Bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPage(currentPage);
  }, [currentPage, loadPage]);

  const pageNumbers = Array.from({ length: Math.max(0, totalPages) }, (_, i) => i + 1);

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="mb-0">Taleplerim</h5>
      </div>
      <div className="card-body p-0">
        {error && (
          <div className="alert alert-warning rounded-0 mb-0 py-2" role="alert">
            {error}
          </div>
        )}
        {loading ? (
          <div className="d-flex align-items-center justify-content-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Yükleniyor...</span>
            </div>
          </div>
        ) : (
          <>
            <ul className="list-group list-group-flush">
              {tickets.map((t) => (
                <li key={t.id} className="list-group-item d-flex justify-content-between align-items-center">
                  <div>
                    <Link to={detailPath(t.id)} className="fw-medium text-decoration-none">
                      #{t.id.slice(0, 8)}… – {t.supportCategoryName ?? 'Destek'}
                    </Link>
                    <small className="text-muted d-block">
                      {t.supportStatusName ?? 'Beklemede'} · {t.createdAtUtc ? new Date(t.createdAtUtc).toLocaleDateString('tr-TR') : ''}
                    </small>
                  </div>
                  <Link to={detailPath(t.id)} className="btn btn-sm btn-outline-primary">
                    Detay
                  </Link>
                </li>
              ))}
            </ul>
            {!loading && tickets.length === 0 && (
              <div className="text-muted text-center py-4">Henüz talebiniz yok.</div>
            )}
            {totalPages > 1 && (
              <nav className="p-3 border-top" aria-label="Sayfalar">
                <ul className="pagination pagination-sm mb-0 justify-content-center">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <Button
                      className="page-link"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      Önceki
                    </Button>
                  </li>
                  {pageNumbers.map((num) => (
                    <li key={num} className={`page-item ${currentPage === num ? 'active' : ''}`}>
                      <Button className="page-link" onClick={() => setCurrentPage(num)}>
                        {num}
                      </Button>
                    </li>
                  ))}
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <Button
                      className="page-link"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Sonraki
                    </Button>
                  </li>
                </ul>
              </nav>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SupportTicketList;
